import { signalStore, withState, withMethods, patchState } from '@ngrx/signals';
import {
    getTableColumns,
    resetTableColumns,
    saveTableColumns,
} from '@app/shared/services/table.cols';

/**
 * Per-user, per-view table column-visibility preferences.
 *
 * Extracted out of AppStore so global app/session state stays focused. Persistence
 * is delegated to the `table.cols` service (localStorage); this store mirrors it in a
 * signal keyed by `${viewType}_${userRole}`.
 */
export interface TablePreferencesState {
    columnVisibility: { [key: string]: string[] };
}

const initialState: TablePreferencesState = {
    columnVisibility: {},
};

export const TablePreferencesStore = signalStore(
    { providedIn: 'root' },
    withState<TablePreferencesState>(initialState),
    withMethods(store => ({
        setColumnVisibility(
            uid: string,
            viewKey: string,
            visibleColumns: string[]
        ): void {
            saveTableColumns(uid, viewKey, visibleColumns);
            patchState(store, state => ({
                columnVisibility: {
                    ...state.columnVisibility,
                    [viewKey]: visibleColumns,
                },
            }));
        },

        getVisibleColumns(
            uid: string,
            viewType: string,
            userRole: string
        ): string[] {
            const viewKey = `${viewType}_${userRole}`;
            return (
                store.columnVisibility()[viewKey] ||
                (getTableColumns(uid, viewKey) as string[]) ||
                []
            );
        },

        hasColumnVisibilitySettings(
            uid: string,
            viewType: string,
            userRole: string
        ): boolean {
            const viewKey = `${viewType}_${userRole}`;
            return (
                viewKey in store.columnVisibility() ||
                getTableColumns(uid, viewKey) !== null
            );
        },

        clearColumnVisibility(
            uid: string,
            viewType: string,
            userRole: string
        ): void {
            const viewKey = `${viewType}_${userRole}`;
            resetTableColumns(uid, viewKey);
            patchState(store, state => {
                const newColumnVisibility = {
                    ...state.columnVisibility,
                };
                delete newColumnVisibility[viewKey];
                return {
                    columnVisibility: newColumnVisibility,
                };
            });
        },
    }))
);
